import React, { createContext, useContext, useState, ReactNode } from "react";

export type Role = "SUPERADMIN" | "OWNER" | "ADMIN" | "MANAGER" | "USER";

export interface AuthState {
  accessToken: string | null;
  role: Role | null;
  orgId: string | null;
  isAuthenticated: boolean;
}

export interface AuthContextType extends AuthState {
  login: (token: string, role: Role, orgId: string | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem("access_token"),
  );
  const [role, setRole] = useState<Role | null>(
    (localStorage.getItem("role") as Role) || null,
  );
  const [orgId, setOrgId] = useState<string | null>(
    localStorage.getItem("org_id"),
  );

  const login = (token: string, role: Role, orgId: string | null) => {
    setAccessToken(token);
    setRole(role);
    setOrgId(orgId);

    localStorage.setItem("access_token", token);
    localStorage.setItem("role", role);
    if (orgId) {
      localStorage.setItem("org_id", orgId);
    } else {
      localStorage.removeItem("org_id");
      localStorage.removeItem("token");
    }
  };

  const logout = () => {
    setAccessToken(null);
    setRole(null);
    setOrgId(null);

    localStorage.removeItem("access_token");
    localStorage.removeItem("role");
    localStorage.removeItem("org_id");
    localStorage.removeItem("token");
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        role,
        orgId,
        isAuthenticated: !!accessToken,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook to easily use Auth in other components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
